<script>
import * as sdk from '../../functions/sdk';
import { store } from '../../functions/mixins';
import { loadImage } from '../../functions/helpers';
import UiButton from '../ui/Button';
import UiLoading from '../ui/Loading';

export const DEVICE_ANDROID             = 'D_ANDROID';
export const DEVICE_IPHONE              = 'D_IPHONE';
export const DEVICE_APPLEWATCH          = 'D_APPLEWATCH';

export const BUBBLE_MODE_NORMAL         = 'BM_NORMAL';
export const BUBBLE_MODE_ANDROIDBANNER  = 'BM_ANDROIDBANNER';

export const MESSAGE_TYPE_TEXT          = 'MT_TEXT';
export const MESSAGE_TYPE_IFRAME        = 'MT_IFRAME';
export const MESSAGE_TYPE_IMAGES        = 'MT_IMAGES';
export const MESSAGE_TYPE_SEYU2ABCD     = 'MT_SEYU2ABCD';

export const VIEW_NONE                  = 'V_NONE';
export const VIEW_BUBBLE                = 'V_BUBBLE';
export const VIEW_TRANSITION            = 'V_TRANSITION';
export const VIEW_MESSAGE               = 'V_MESSAGE';
export const VIEW_BANNER                = 'V_BANNER';
export const VIEW_AUTOPLAY              = 'V_AUTOPLAY';
export const VIEW_ANDROID_BANNER        = 'V_ANDROID_BANNER';
export const VIEW_ANDROID_ADV           = 'V_ANDROID_ADV';
export const VIEW_ANDROID_EXT_BANNER    = 'V_ANDROID_EXTENDED_BANNER';
export const VIEW_ANDROID_BUBBLE        = 'V_ANDROID_BUBBLE';
export const VIEW_IOS_EXT_NOTIFICATION  = 'V_IOS_EXTENDED_NOTIFICATION';
export const VIEW_IOS_EXT_VISUAL_NOTIFICATION  = 'V_IOS_EXTENDED_VISUAL_NOTIFICATION';
export const VIEW_IOS_APPLE_WATCH       = 'V_IOS_APPLE_WATCH';

export const EVENT_NAV_PREV             = 'onPrevClick';
export const EVENT_NAV_NEXT             = 'onNextClick';

export const VIEW_DURATION_BUBBLE       = 4000;
export const VIEW_DURATION_TRANSITION   = 2000;

export default {
    components: {
        UiButton,
        UiLoading
    },
    mixins: [store],
    props: {
        message: [Object, Array],
        view: {
            type: String,
            default: VIEW_AUTOPLAY,
        },
        device: {
            type: String,
            default: DEVICE_ANDROID,
        },
        showNavigation: Boolean,
        navigationPosition: {
            type: String,
            default: 'Center'
        },
        showDeviceSelect: Boolean,
        deviceSelectPosition: {
            type: String,
            default: 'Right'
        },
        maxWidth: {
            type: Number,
            default: 300,
        },
        maxHeight: {
            type: Number,
            default: 500,
        },
        banner: Object,
        androidBanner: String,
        androidAdv: String,
        androidExtBanner: String,
        androidBubble: String,
        iosExtNotification: String,
        iosExtVisualNotification: String,
        iosAppleWatch: String,
        altAndroidScreenBackground: Boolean,
        forceLoading: Boolean,
    },
    data: () => ({
        assets: {},
        loadingRequiredAssets: false,
        loadingAssets: false,
        errorRequiredAssets: false,
        assetsError: false,
        animation: 0,
        timeout: null,
        scale: 1,
        isAndroidAdvLong: false,
    }),
    created() {
        this.loadingRequiredAssets = true;
        Promise.all([
            loadImage('/static/img/toolbox/android_mockup.png', 'android_mockup'),
            loadImage('/static/img/toolbox/iphone_mockup.png', 'iphone_mockup'),
            loadImage('/static/img/toolbox/applewatch_mockup.png', 'applewatch_mockup'),
            loadImage('/static/img/toolbox/android_screen.png', 'android_screen'),
            loadImage('/static/img/toolbox/android_lock_screen.jpg', 'android_lock_screen'),
            loadImage('/static/img/toolbox/iphone_screen.png', 'iphone_screen'),
        ]).then(images => images.forEach(i => {
            this.assets[i.tag] = i;
            if (!i.success) {
                this.errorRequiredAssets = true;
            }
        })).finally(() => {
            this.loadingRequiredAssets = false;
        });
    },
    computed: {
        mockupImage() {
            switch (this.device) {
                case DEVICE_ANDROID:
                    return this.assets.android_mockup;
                case DEVICE_IPHONE:
                    return this.assets.iphone_mockup;
                case DEVICE_APPLEWATCH:
                    return this.assets.applewatch_mockup;
            }
        },
        screenImage() {
            switch (this.device) {
                case DEVICE_ANDROID:
                    return this.altAndroidScreenBackground
                        ? this.assets.android_screen
                        : this.assets.android_lock_screen;
                case DEVICE_IPHONE:
                    return this.assets.iphone_screen;
                case DEVICE_APPLEWATCH:
                    return null;
            }
        },
        mockupStyle() {
            const {
                mockupImage,
                maxWidth,
                maxHeight
            } = this;
            const scale = this.getImageScale(mockupImage, maxWidth, maxHeight);
            return {
                width: `${mockupImage.width * scale}px`,
                height: `${mockupImage.height * scale}px`,
            };
        },
        screenStyle() {
            const {
                screenImage,
                mockupImage,
                maxWidth,
                maxHeight
            } = this;
            const scale = this.getImageScale(mockupImage, maxWidth, maxHeight);
            switch(this.device) {
                case DEVICE_ANDROID:
                    return {
                        width: `${660 * scale}px`,
                        height: `${1370 * scale}px`,
                        transform: `translate(-50%, calc(-50% - ${5 * scale}px))`,
                        borderRadius: `${40 * scale}px`,
                    };
                case DEVICE_IPHONE:
                    return {
                        width: `${612 * scale}px`,
                        height: `${1322 * scale}px`,
                        borderRadius: `${40 * scale}px`,
                    };
                case DEVICE_APPLEWATCH:
                    return {
                        width: `${520 * scale}px`,
                        height: `${520 * scale}px`,
                        transform: `translate(calc(-50% - ${15 * scale}px), calc(-50% + ${36 * scale}px))`,
                    };
            }
        },
        currentView() {
            if (this.view !== 'V_AUTOPLAY') {
                return this.view;
            }
            switch (this.animation) {
                case 0:
                    return 'V_ANDROID_BUBBLE';
                case 1:
                    return 'V_BANNER';
            }
        },
        messagesArray() {
            return Array.isArray(this.message) ? this.message : [this.message];
        },
        isMessageVisible() {
            return ['V_MESSAGE', 'V_BANNER'].includes(this.currentView);
        },
        isBannerVisible() {
            return this.currentView === 'V_BANNER' && this.banner;
        },
    },
    methods: {
        setDevice(device) {
            this.device = device;
        },
        getImageScale(image, maxWidth, maxHeight) {
            let sf = 1;
            if (maxWidth < image.width) {
                sf = maxWidth / image.width;
            }
            if (maxHeight < image.height && sf > maxHeight / image.height) {
                sf = maxHeight / image.height;
            }
            return sf;
        },
        getImageSrc(img) {
            if (typeof img === 'string') {
                return img;
            } else if (img instanceof Image) {
                return img.src;
            } else if (img instanceof File || img instanceof Blob) {
                return URL.createObjectURL(img);
            } else {
                return null;
            }
        },
        startPlayback() {
            this.animation = 0;
            if (this.timeout) {
                clearTimeout(this.timeout);
                this.timeout = null;
            }
            this.timeout = setTimeout(() => {
                this.animation++;
            }, this.androidBubble ? VIEW_DURATION_BUBBLE : 0);
        },
        restart() {
            if (this.view === 'V_AUTOPLAY') {
                this.startPlayback();
            }
        },
        isValidUrl(url) {
            return url.indexOf('http://') === 0 || url.indexOf('https://') === 0;
        },
        isMP4(filename) {
            return filename && filename.split('.').pop().toLowerCase() === 'mp4';
        },
        onAndroidAdvLoad(e) {
            this.isAndroidAdvLong = e.target.naturalWidth > 277;
        },
    },
    watch: {
        view: {
            immediate: true,
            handler(nextVal) {
                if (nextVal === 'V_AUTOPLAY') {
                    this.startPlayback();
                }
            },
        },
        banner: {
            deep: true,
            immediate: true,
            handler(nextVal) {
                this.assets.banner = null;
                if (nextVal && nextVal.url) {
                    this.loadingAssets = true;
                    this.$forceUpdate();
                    loadImage(nextVal.url).then(image => {
                        this.assets.transition = image;
                    }).finally(() => {
                        this.loadingAssets = false;
                        this.$forceUpdate();
                    });
                } else {
                    this.$forceUpdate();
                }
            },
        },
    },
}
</script>

<template>
    <div class="PhonePreview" ref="container">
        <ui-loading v-if="loadingRequiredAssets" />
        <div class="Mockup" :style="mockupStyle" @click="restart()" v-else>
            <img
                class="MockupImage"
                :src="mockupImage.src"
            />
            <div class="Screen" :style="screenStyle">
                <img
                    v-if="screenImage"
                    class="ScreenImage"
                    :src="screenImage.src"
                />
                <div class="ViewContainer" v-if="view === 'V_ANDROID_BANNER' || view === 'V_ANDROID_ADV'">
                    <div class="ViewAndroidBannerAndAdv">
                        <img :src="androidBanner" />
                        <img :src="androidAdv" @load="onAndroidAdvLoad" :style="{'z-index': isAndroidAdvLong ? -1 : 1}" />
                    </div>
                </div>
                <div class="ViewContainer" v-if="view === 'V_ANDROID_EXTENDED_BANNER'">
                    <div class="ViewAndroidBannerAndAdv HideAfterSec" :key="`1-${androidExtBanner}`">
                        <img :src="androidBanner" />
                        <img :src="androidAdv" @load="onAndroidAdvLoad" :style="{'z-index': isAndroidAdvLong ? -1 : 1}" />
                    </div>
                    <div class="ViewAndroidExtendedBanner" :key="`2-${androidExtBanner}`">
                        <img :src="androidExtBanner" />
                    </div>
                    <!--
                    <div class="ViewAndroidExtendedBanner" v-if="androidExtBanner">
                        <div class="ViewAndroidExtendedBannerStatus">
                            <span class="mdi mdi-wifi"></span>
                            <span class="mdi mdi-signal"></span>
                            <span>50%</span>
                            <span class="mdi mdi-battery-30"></span>
                        </div>
                        <div class="ViewAndroidExtendedBannerClock">
                            <span>
                                12:56 Fri 20 March
                            </span>
                            <span>
                                <span class="mdi mdi-magnify"></span>
                                <span class="mdi mdi-settings"></span>
                            </span>
                        </div>
                        <div class="ViewAndroidExtendedBannerActions">
                            <span class="Active"><span class="mdi mdi-wifi"></span></span>
                            <span class="Active"><span class="mdi mdi-volume-high"></span></span>
                            <span><span class="mdi mdi-bluetooth"></span></span>
                            <span><span class="mdi mdi-flashlight"></span></span>
                            <span><span class="mdi mdi-airplane"></span></span>
                        </div>
                        <div class="ViewAndroidExtendedBannerHandle">
                            <span></span>
                        </div>
                        <div class="ViewAndroidExtendedBannerContent">
                            <img :src="androidExtBanner" />
                        </div>
                        <div class="ViewAndroidExtendedBannerFooter"></div>
                    </div>
                    -->
                </div>
                <div class="ViewContainer" v-if="view === 'V_ANDROID_BUBBLE'">
                    <div class="ViewAndroidBubble">
                        <video v-if="isMP4(androidBubble)" autoplay loop>
                            <source :src="androidBubble" type="video/mp4">
                            <span>Video not supported.</span>
                        </video>
                        <img v-else :src="androidBubble" />
                    </div>
                </div>
                <div class="ViewContainer" v-if="['V_IOS_EXTENDED_NOTIFICATION', 'V_IOS_EXTENDED_VISUAL_NOTIFICATION'].includes(view)">
                    <div class="ViewIOSExtendedNotification" v-if="iosExtNotification">
                        <div class="IPhoneCard">
                            <div class="IPhoneCardHeader">
                                <span>&times;</span>
                            </div>
                            <div class="IPhoneCardContent">
                                <template v-if="view === 'V_IOS_EXTENDED_NOTIFICATION'">
                                    <video v-if="isMP4(iosExtNotification)" autoplay loop>
                                        <source :src="iosExtNotification" type="video/mp4">
                                        <span>Video not supported.</span>
                                    </video>
                                    <img v-else :src="iosExtNotification" />
                                </template>
                                <div :class="{FlipBox: true, FlipBoxAnim: !!iosExtVisualNotification}" v-else>
                                    <div class="FlipBoxInner">
                                        <div class="FlipBoxFront">
                                            <video v-if="isMP4(iosExtNotification)" autoplay loop>
                                                <source :src="iosExtNotification" type="video/mp4">
                                                <span>Video not supported.</span>
                                            </video>
                                            <img v-else :src="iosExtNotification" />
                                        </div>
                                        <div class="FlipBoxBack">
                                            <video v-if="isMP4(iosExtVisualNotification)" autoplay loop>
                                                <source :src="iosExtVisualNotification" type="video/mp4">
                                                <span>Video not supported.</span>
                                            </video>
                                            <img v-else :src="iosExtVisualNotification" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="IPhoneCardFooter"></div>
                        </div>
                        <div class="IPhoneButton">View Details</div>
                    </div>
                </div>
                <div class="ViewContainer" v-if="view === 'V_IOS_APPLE_WATCH'">
                    <div :class="['ViewIOSAppleWatch', {ViewIOSAppleWatchLoading: forceLoading}]" :style="{'background-image': `url('${iosAppleWatch}')`}">
                        <ui-loading v-if="forceLoading" />
                    </div>
                </div>
                <template v-if="isMessageVisible">
                    <div class="ViewMessage" v-for="(m, i) in messagesArray" :key="i">
                        <div class="MTText" v-if="m.type  === 'MT_TEXT'" :style="m.payload.style || {}">
                            <span :style="m.payload.innerStyle || {}">
                                {{ m.payload.text || '' }}
                            </span>
                        </div>
                        <div class="MTIFrame" v-if="m.type  === 'MT_IFRAME' && isValidUrl(m.payload)">
                            <iframe
                                :src="m.payload"
                                sandbox="allow-scripts allow-same-origin"
                                scrolling="no"
                            ></iframe>
                        </div>
                        <div class="MTImages" v-if="m.type  === 'MT_IMAGES'">
                            <img
                                v-for="(i, k) in m.payload"
                                :src="getImageSrc(i)"
                                :key="k"
                            />
                        </div>
                        <div class="MTSEYU2ABCD" v-if="m.type  === 'MT_SEYU2ABCD'">
                            <div :style="{color: m.payload.colors.q}">{{ m.payload.q  }}</div>
                            <ul>
                                <li :style="{color: m.payload.colors.a}">
                                    <strong>A</strong>
                                    <span>{{ m.payload.a }}</span>
                                </li>
                                <li :style="{color: m.payload.colors.b}">
                                    <strong>B</strong>
                                    <span>{{ m.payload.b }}</span>
                                </li>
                                <li :style="{color: m.payload.colors.c}">
                                    <strong>C</strong>
                                    <span>{{ m.payload.c }}</span>
                                </li>
                                <li :style="{color: m.payload.colors.d}">
                                    <strong>D</strong>
                                    <span>{{ m.payload.d }}</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </template>
                <div class="ViewBanner" v-if="isBannerVisible">
                    <img :src="banner.url" v-if="banner.url" />
                    <iframe
                        v-else
                        :src="banner.link"
                        sandbox="allow-scripts allow-same-origin"
                        scrolling="no"
                    ></iframe>
                </div>
            </div>
            <div class="Overlay" v-if="view === 'V_AUTOPLAY'">
                <span class="mdi mdi-refresh"></span>
            </div>
            <div :class="['Navigation', navigationPosition]" v-if="showNavigation">
                <a href="javascript:;" class="Prev" @click="$emit('onPrevClick')">
                    <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid" width="8" height="14" viewBox="0 0 8 14" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M.287 7.694l6.04 6.022a.987.987 0 001.392 0 .979.979 0 000-1.387L2.374 7l5.345-5.328a.979.979 0 000-1.387.987.987 0 00-1.392 0L.287 6.307a.983.983 0 000 1.387z"/></svg>
                </a>
                <a href="javascript:;" class="Next" @click="$emit('onNextClick')">
                    <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid" width="8.03" height="14" viewBox="0 0 8.03 14" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M7.741 6.305L1.678.283a.993.993 0 00-1.397 0 .977.977 0 000 1.387l5.366 5.328-5.366 5.329a.977.977 0 000 1.387.993.993 0 001.397 0l6.063-6.022a.975.975 0 000-1.387z"/></svg>
                </a>
            </div>
            <!--
            <div :class="['DeviceSelect', deviceSelectPosition]" v-if="showDeviceSelect">
                <a
                    href="javascript:;"
                    @click="setDevice('D_ANDROID', 'BM_ANDROIDBANNER')"
                    :class="{Active: device === 'D_ANDROID' && bubbleMode === 'BM_ANDROIDBANNER'}"
                >
                    <img src="/static/img/toolbox/icon_android_banner.png" />
                </a>
                <a
                    href="javascript:;"
                    @click="setDevice('D_APPLEWATCH')"
                    :class="{Active: device === 'D_APPLEWATCH'}"
                >
                    <img src="/static/img/toolbox/icon_applewatch.png" />
                </a>
                <a
                    href="javascript:;"
                    @click="setDevice('D_ANDROID', 'BM_NORMAL')"
                    :class="{Active: device === 'D_ANDROID' && bubbleMode === 'BM_NORMAL'}"
                >
                    <img src="/static/img/toolbox/icon_android_normal.png" />
                </a>
                <a
                    href="javascript:;"
                    @click="setDevice('D_IPHONE')"
                    :class="{Active: device === 'D_IPHONE'}"
                >
                    <img src="/static/img/toolbox/icon_iphone.png" />
                </a>
            </div>
            -->
        </div>
    </div>
</template>

<style lang="stylus">

@keyframes fadeIn {
    from { opacity: 0 }
    to { opacity: 1 }
}

@keyframes fadeOut {
    from { opacity: 1 }
    to { opacity: 0 }
}

@keyframes flipBoxAnimation {
    0% { transform: rotateY(0deg); }
    40% { transform: rotateY(0deg); }
    60% { transform: rotateY(180deg); }
    100% { transform: rotateY(180deg); }
}

.PhonePreview
    position relative
    display flex
    justify-content center
    width 100%
    *
        box-sizing border-box
    .Mockup
        position relative
        top 0
        left 0
        .MockupImage
            position absolute
            z-index 2
            max-width 100%
            max-height 100%
        .Screen
            position absolute
            z-index 1
            top 50%
            left 50%
            transform translate(-50%, -50%)
            overflow hidden
            background #333
            .ScreenImage
                width 100%
                height 100%
            .ViewContainer
                position absolute
                top 0
                left 0
                width 100%
                height 100%
            .ViewAndroidBannerAndAdv
                position absolute
                top 50%
                left 0
                width 100%
                transform translate(0, -50%)
                img:nth-child(1)
                    display block
                    width 100%
                    height auto
                img:nth-child(2)
                    display block
                    position absolute
                    top 0px
                    right 0px
                    height 100%
                    width auto
                &.HideAfterSec
                    animation-name fadeOut
                    animation-duration 1s
                    animation-delay 1s
                    animation-fill-mode forwards
            .ViewAndroidExtendedBanner
                position absolute
                top 50%
                left 50%
                width 100%
                height auto
                transform translate(-50%, -50%)
                animation-name fadeIn
                animation-duration 1s
                animation-delay 1s
                animation-fill-mode forwards
                opacity 0
                img
                    width 100%
                    height auto

            .ViewAndroidExtendedBanner__OLDER
                position absolute
                top 0
                left 0
                width 100%
                height 100%
                display flex
                flex-direction column
                &Status
                    display flex
                    justify-content flex-end
                    padding 7px 35px 7px 10px
                    background #F2F2F2
                    color #626262
                    span
                        margin-left 5px
                &Clock
                    background #F2F2F2
                    display flex
                    justify-content space-between
                    padding 5px 10px
                    color #262626
                    font-size 1.3em
                    & > span:last-child span:last-child
                        margin-left 10px
                &Actions
                    background #F2F2F2
                    display flex
                    justify-content center
                    padding 10px 10px
                    & > span
                        display flex
                        justify-content center
                        align-items center
                        background #E7E6E9
                        width 32px
                        height 32px
                        border-radius 50%
                        font-size 18px
                        margin 0 5px
                        &.Active
                            background #4399FF
                            color #FFFFFF
                &Handle
                    background #F2F2F2
                    display flex
                    justify-content center
                    span
                        display block
                        background #AFAFB1
                        height 3px
                        width 20px
                        margin 5px
                &Content
                    background #F2F2F2
                    img
                        display block
                        width 100%
                        height auto
                &Footer
                    background #FEFEFC
                    border-top 2px solid #EFEDEB
                    border-radius 0 0 20px 20px
                    overflow hidden
                    height 30px
            .ViewAndroidBubble
                position absolute
                top 50%
                right 20px
                max-width 60px
                max-height 60px
                transform translate(0, -50%)
                img, video
                    width 100%
            .ViewIOSExtendedNotification
                width 100%
                height 100%
                padding 40px 10px 0
                box-sizing border-box
                .IPhoneCard
                    background #fff
                    margin-bottom 20px
                    border 2px solid #EFEEEE
                    border-radius 10px
                    .IPhoneCardHeader
                        display flex
                        align-items center
                        height 35px
                        border-bottom 2px solid #EFEEEE
                        padding 0 10px
                        color #808080
                        span
                            font-size 32px
                            margin-left auto
                    .IPhoneCardContent
                        display flex
                        align-items center
                        justify-content center
                        padding 5px 0
                        img, video
                            max-width calc(100% - 10px)
                    .IPhoneCardFooter
                        border-top 2px solid #EFEEEE
                        padding 0 10px
                        height 35px
                .IPhoneButton
                    background rgba(255,255,255,.75)
                    border-radius 10px
                    text-align center
                    padding 14px 0
                    color #333
                    font-weight 500
            .ViewIOSAppleWatch
                width 100%
                height 100%
                background-size contain
                background-repeat no-repeat
                background-position bottom center
                display flex
                align-items center
                justify-content center
                &.ViewIOSAppleWatchLoading
                    background-color #fff
            .ViewMessage
                position absolute
                top 0
                left 0
                width 100%
                height 100%
                .MTText
                    overflow hidden
                    width 100%
                    height 100%
                    color #181834
                    display flex
                    flex-direction column
                    align-items center
                    span
                        position absolute
                        top 50%
                        left 50%
                        transform translate(-50%,-50%)
                .MTIFrame
                    overflow hidden
                    width 100%
                    height 100%
                    background #fff
                    iframe
                        width 200%
                        height 200%
                        transform scale(.5)
                        transform-origin top left
                .MTImages
                    overflow hidden
                    width 100%
                    height 100%
                    img
                        position absolute
                        top 0
                        left 0
                        width 100%
                        height 100%
                .MTSEYU2ABCD
                    color #00A551
                    div
                        position absolute
                        top 22%
                        padding 5px 20px
                        background white
                        margin 0 5px
                        box-shadow: 0 2px 5px rgba(0,0,0,.6)
                        font-weight: bold
                    ul
                        position absolute
                        top 40%
                        bottom 0
                        left 0
                        right 0
                        padding 20px
                        margin 0
                        li
                            position relative
                            display flex
                            align-items center
                            background #fff
                            padding 0
                            margin 0 0 20px
                            list-style none
                            border-radius 4px
                            box-shadow: 0 2px 5px rgba(0,0,0,.6)
                            strong
                                font-size 24px
                                padding 4px 10px
                                text-shadow: 0 1px 3px #000
                            span
                                flex 1 1 auto
                                text-align center
                                font-size 12px
                                padding 10px 10px 10px 0



            .ViewBanner
                position absolute
                bottom 0
                left 0
                width 100%
                iframe
                    border none
                    width 100%
                    height 100px
                img
                    display block
                    width 100%
                    max-height 100px
        .Overlay
            position absolute
            top 0
            right 0
            bottom 0
            left 0
            background rgba(255,255,255,.5)
            display flex
            align-items center
            justify-content center
            color #fff
            font-size 32px
            opacity 0
            transition .3s all
            z-index 3
            cursor pointer
        &:hover .Overlay
            opacity 1
    .Navigation
        z-index 3
        position absolute
        top 50%
        left 0
        right 0
        a
            display flex
            align-items center
            justify-content center
            position absolute
            border-radius 50%
            background #ededed
            font-weight 700
            color #181834
            width 54px
            height 54px
            background #fff
            &.Prev
                left 0
                top 0
                transform translate(calc(-50% - 15px), -50%)
            &.Next
                left 100%
                top 0
                transform translate(calc(-50% + 15px), -50%)
    .DeviceSelect
        position absolute
        top 50%
        left calc(100% + 60px)
        transform translate(0, -50%)
        &.Left
            left auto
            right calc(100% + 60px)
        a
            outline 0
            display block
            margin-bottom 10px
            &:last-child
                margin-bottom 0px
            img
                max-width 32px
                opacity .35
                transition .3s all
            &.Active img, &:hover img
                opacity 1
    .FlipBox
        position: relative
        perspective: 1000px;
        width: 100%;
        padding-top: 85.665914221%;
        .FlipBoxInner
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            transition: transform 0.8s;
            transform-style: preserve-3d;
            .FlipBoxFront, .FlipBoxBack
                background: #fff;
                position: absolute;
                width: 100%;
                height: 100%;
                backface-visibility: hidden;
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: hidden;
                img, video
                    display: block;
                    object-fit: fill;
            .FlipBoxBack
                transform: rotateY(180deg);
        &.FlipBoxAnim .FlipBoxInner
            animation-name: flipBoxAnimation;
            animation-direction: alternate;
            animation-duration: 2.5s;
            animation-iteration-count: infinite;
</style>
